import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Square, Volume2 } from 'lucide-react';
import { Slider } from './ui/slider';

export const AudioNarration = ({ text, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [rate, setRate] = useState(0.9);
  const [supported, setSupported] = useState(true);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const utteranceRef = useRef(null);

  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      setSupported(false);
      return;
    }

    // Load voices - they may be async
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        setVoicesLoaded(true);
      }
    };

    loadVoices();
    
    // Chrome loads voices asynchronously
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    // Reset when text changes
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setIsPaused(false);
    }
  }, [text]);

  const cleanText = (rawText) => {
    return rawText
      .replace(/\n\n/g, '. ')
      .replace(/\n/g, ' ')
      .replace(/•/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const handlePlay = useCallback(() => {
    if (!window.speechSynthesis) return;

    if (isPaused && utteranceRef.current) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      setIsPlaying(true);
      return;
    }

    // Cancel any existing speech
    window.speechSynthesis.cancel();
    
    const fullText = title ? `${title}. ${cleanText(text)}` : cleanText(text);
    const utterance = new SpeechSynthesisUtterance(fullText);
    utterance.rate = rate;
    utterance.pitch = 1;
    utterance.volume = 1;

    // Select best available voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Female') || 
      v.name.includes('Samantha') || 
      v.name.includes('Victoria') ||
      v.name.includes('Karen') ||
      v.name.includes('Moira') ||
      v.name.includes('Google')
    ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    utterance.onstart = () => {
      setIsPlaying(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      utteranceRef.current = null;
    };

    utterance.onerror = (event) => {
      console.log('Speech error:', event.error);
      setIsPlaying(false);
      setIsPaused(false);
      utteranceRef.current = null;
    };

    utteranceRef.current = utterance;
    
    // Small delay to ensure proper state update
    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 10);
  }, [isPaused, rate, text, title]);

  const handlePause = () => {
    if (window.speechSynthesis && isPlaying) {
      window.speechSynthesis.pause();
      setIsPaused(true);
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setIsPaused(false);
    }
  };

  if (!supported) {
    return null;
  }

  return (
    <div 
      data-testid="audio-narration"
      className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-100 shadow-sm"
    >
      <div className="flex items-center gap-2">
        {!isPlaying ? (
          <button
            onClick={handlePlay}
            data-testid="audio-play-btn"
            className="w-10 h-10 flex items-center justify-center bg-sage text-white rounded-full hover:bg-sage-dark transition-colors"
            title={isPaused ? "Resume" : "Play"}
          >
            <Play className="w-5 h-5 ml-0.5" />
          </button>
        ) : (
          <button
            onClick={handlePause}
            data-testid="audio-pause-btn"
            className="w-10 h-10 flex items-center justify-center bg-sage text-white rounded-full hover:bg-sage-dark transition-colors"
            title="Pause"
          >
            <Pause className="w-5 h-5" />
          </button>
        )}
        <button
          onClick={handleStop}
          data-testid="audio-stop-btn"
          className="w-8 h-8 flex items-center justify-center text-charcoal-light hover:text-sage rounded-full hover:bg-sage/10 transition-colors"
          title="Stop"
        >
          <Square className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 flex items-center gap-3">
        <Volume2 className="w-4 h-4 text-charcoal-light flex-shrink-0" />
        <div className="flex-1 max-w-32">
          <Slider
            data-testid="audio-speed-slider"
            value={[rate * 100]}
            onValueChange={(value) => setRate(value[0] / 100)}
            min={50}
            max={150}
            step={10}
            className="w-full"
          />
        </div>
        <span className="text-xs text-charcoal-light w-12">{rate.toFixed(1)}x</span>
      </div>

      <span className="text-sm text-charcoal-light hidden sm:block">
        {isPlaying ? 'Playing...' : isPaused ? 'Paused' : 'Listen to chapter'}
      </span>
    </div>
  );
};
